import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { CustomTableRow, RowLabelCell, CustomTableCell } from '../CustomTableComponents';
import EmptyCols from '../EmptyCols';
import { Indicators } from '../../../+store/aToF';
import { formatNumberOrDash, getFilteredIndicators, d3EmptyIndicators } from '../utils';
import { Button } from '@material-ui/core';
import classNames from 'classnames';

const isDemo = process.env.REACT_APP_THEME === 'demo';

const styles = (theme) => ({
  metTarget: {
    backgroundColor: '#92FFBE',
  },
  didNotMeet: {
    backgroundColor: '#FF5656',
  },
  boldCell: {
    fontWeight: 'bold',
    fontSize: '16px',
  },

  circle: {
    margin: '10px',
    display: 'inline-block',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    border: isDemo ? '1.5px solid #fff' : '2px solid white',
    background: 'white',
  },
  circle_0: {
    border: isDemo ? '1.5px solid #F37272' : '2px solid red',
    ...(isDemo ? { background: '#fff' } : {}),
  },
  circle_1: {
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(00deg,white 50%, transparent 50%),linear-gradient(90deg, red 50%, transparent 50%)',
    ...(isDemo ? { background: 'conic-gradient(#F37272 0% 25%, #ECEFF0 25% 100%)' } : {}),
  },
  circle_2: {
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(-90deg, transparent  50%, transparent 50%),linear-gradient(90deg, yellow 50%, transparent 50%);',
    ...(isDemo ? { background: 'conic-gradient(#E7C53C 0% 50%, #ECEFF0 50% 100%)' } : {}),
  },
  circle_3: {
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(180deg, transparent 50%, yellow 50%), linear-gradient(90deg, yellow 50%, transparent 50%);',
    ...(isDemo ? { background: 'conic-gradient(#E7C53C 0% 75%, #ECEFF0 75% 100%)' } : {}),
  },
  circle_4: {
    background: isDemo ? '#4CA66B' : 'green',
    // white digit only on the solid green circle; lighter circles keep the default dark digit
    ...(isDemo ? { color: '#fff' } : {}),
  },
  circle_Number: isDemo
    ? {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    : {
        position: 'relative',
        top: '50%',
        transform: 'translateY(-50%)',
      },
  circle_disabled_0: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
  },
  circle_disabled_1: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(00deg,white 50%, transparent 50%),linear-gradient(90deg, #D9D9D9 50%, transparent 50%)',
    ...(isDemo ? { background: 'conic-gradient(#E2E2E2 0% 25%, #ECEFF0 25% 100%)' } : {}),
  },
  circle_disabled_2: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(-90deg, transparent  50%, transparent 50%),linear-gradient(90deg, #D9D9D9 50%, transparent 50%);',
    ...(isDemo ? { background: 'conic-gradient(#E2E2E2 0% 50%, #ECEFF0 50% 100%)' } : {}),
  },
  circle_disabled_3: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
    backgroundImage: isDemo
      ? undefined
      : 'linear-gradient(180deg, transparent 50%, #D9D9D9 50%), linear-gradient(90deg, #D9D9D9 50%, transparent 50%);',
    ...(isDemo ? { background: 'conic-gradient(#E2E2E2 0% 75%, #ECEFF0 75% 100%)' } : {}),
  },
  circle_disabled_4: {
    border: isDemo ? '1.5px solid #E2E2E2' : '2px dotted #333',
    background: isDemo ? '#E2E2E2' : '#D9D9D9',
  },
  circle_NumberDisabled: {
    color: isDemo ? '#777' : '#555',
    fontWeight: 'bold',
  },
});
const getTargetValue = (eligibles, metTargets, indicatorKey, metIndex, showAllTargets, componentCode) => {
  const allTargetsComponents = new Set(['academicAchievement', 'academicGrowth', 'graduationRate', 'ccmr']);
  if (allTargetsComponents.has(componentCode) || (eligibles && eligibles[indicatorKey])) {
    let metKey = `t${metIndex}`;
    if (
      metTargets &&
      metTargets[indicatorKey] &&
      metTargets[indicatorKey].targets &&
      metTargets[indicatorKey].targets[metKey]
    ) {
      var target = metTargets[indicatorKey].targets[metKey];
      var isValidTarget = isValidTargetValue(metTargets[indicatorKey].targets, metIndex);
      if (showAllTargets == true || isValidTarget) {
        if (!isNaN(target)) {
          return target;
        }
      }
    }
  }
  return '-';
};

const isValidTargetValue = (targets, metIndex) => {
  if (targets[`t${metIndex}`] == null || targets[`t${metIndex}`] == 0 || targets[`t${metIndex}`] == 0.0) {
    return false;
  }
  for (let point = metIndex + 1; point < 5; point++) {
    if (targets[`t${metIndex}`] > targets[`t${point}`]) {
      return false;
    }
  }
  return true;
};
const getAcademicGrowthValue = (d2aTables, key) => {
  return d2aTables && d2aTables.count && d2aTables.count.zeroPoint ? `${d2aTables.count.zeroPoint[key]}` : '-';
};

const getAcademicAchievementValue = (totals, counts, key) => {
  return totals[key] > 0 ? `${totals[key] - counts[key]}` : '-';
};
const SubComponentRows = ({
  componentCode,
  label,
  classes,
  totalsColumnsLength,
  scoreColumnsLength,
  counts = {},
  totals = {},
  percents = d3EmptyIndicators,
  targets = {},
  totalIndicators = {},
  eligibles = {},
  metTargets = {},
  handleGetStudentsList,
  studentsListPath,
  profile,
  schoolTypeConversion,
  componentIndicators = [],
  studentsListPathDoesNotMeet,
  showAllTargets,
  d2aTables,
  displayedIndicators,
  showAdditionalStudentGroups,
}) => {
  let indicatorsToUse =
    displayedIndicators && Object.keys(displayedIndicators).length > 0 ? displayedIndicators : Indicators;
  indicatorsToUse = getFilteredIndicators(indicatorsToUse, showAdditionalStudentGroups);
  return (
    <React.Fragment>
      <CustomTableRow>
        <RowLabelCell>{`Included`}</RowLabelCell>
        {Object.entries(indicatorsToUse).map(([key]) => (
          <CustomTableCell key={key}>
            {eligibles && eligibles[key] && componentIndicators && componentIndicators.indexOf(key) >= 0 ? 'Y' : '-'}
          </CustomTableCell>
        ))}
        <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
      </CustomTableRow>

      <CustomTableRow>
        <RowLabelCell>{componentCode == 'academicGrowth' ? `# Full Point and Half Point` : '# Met'}</RowLabelCell>
        {Object.entries(indicatorsToUse).map(([key, indicatorLabel]) =>
          studentsListPath ? (
            <CustomTableCell key={key}>
              <Button
                className={classes.clickableCell}
                color="inherit"
                onClick={() =>
                  handleGetStudentsList(
                    profile.organizationId,
                    `${studentsListPath}/${key}`,
                    componentCode === 'academicGrowth' ? 'Full/Half Point' : 'Met',
                    indicatorLabel
                  )
                }
                disabled={!counts[key]}
              >
                {counts[key] > 0 ? `${counts[key]}` : '-'}
              </Button>
            </CustomTableCell>
          ) : (
            <CustomTableCell key={key}> {counts[key] > 0 ? `${counts[key]}` : '-'} </CustomTableCell>
          )
        )}
        <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
      </CustomTableRow>
      {studentsListPathDoesNotMeet && (
        <CustomTableRow>
          <RowLabelCell>{componentCode == 'academicGrowth' ? `# Zero Point` : '# Not Met'}</RowLabelCell>
          {Object.entries(indicatorsToUse).map(([key, indicatorLabel]) =>
            studentsListPath ? (
              <CustomTableCell key={key}>
                <Button
                  className={classes.clickableCell}
                  color="inherit"
                  onClick={() =>
                    handleGetStudentsList(
                      profile.organizationId,
                      `${studentsListPathDoesNotMeet}/${key}`,
                      componentCode === 'academicGrowth' ? 'Zero Point' : 'Not Met',
                      indicatorLabel
                    )
                  }
                  disabled={!counts[key]}
                >
                  {componentCode == 'academicGrowth'
                    ? getAcademicGrowthValue(d2aTables, key)
                    : getAcademicAchievementValue(totals, counts, key)}
                </Button>
              </CustomTableCell>
            ) : (
              <CustomTableCell key={key}>
                {componentCode == 'academicGrowth'
                  ? getAcademicGrowthValue(d2aTables, key)
                  : getAcademicAchievementValue(totals, counts, key)}
              </CustomTableCell>
            )
          )}
          <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
        </CustomTableRow>
      )}
      <CustomTableRow>
        <RowLabelCell>{`# Total`}</RowLabelCell>
        {Object.entries(indicatorsToUse).map(([key]) => (
          <CustomTableCell key={key}> {totals[key] > 0 ? `${totals[key]}` : '-'} </CustomTableCell>
        ))}
        <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
      </CustomTableRow>
      <CustomTableRow>
        <RowLabelCell className={classes.boldCell}>{`% Met`}</RowLabelCell>
        {Object.entries(indicatorsToUse).map(([key]) => (
          <CustomTableCell className={classes.boldCell} key={key}>
            {`${formatNumberOrDash(percents[key], '%')}`}
          </CustomTableCell>
        ))}
        <CustomTableCell>{formatNumberOrDash(totalIndicators.percent, '%')}</CustomTableCell>
        <EmptyCols length={scoreColumnsLength + scoreColumnsLength} />
      </CustomTableRow>

      <CustomTableRow>
        <RowLabelCell>{`4 Points`}</RowLabelCell>
        {Object.entries(indicatorsToUse).map(([key]) => (
          <CustomTableCell key={key}>
            {getTargetValue(eligibles, metTargets, key, 4, showAllTargets, componentCode)}
          </CustomTableCell>
        ))}
        <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
      </CustomTableRow>
      <CustomTableRow>
        <RowLabelCell>{`3 Points`}</RowLabelCell>
        {Object.entries(indicatorsToUse).map(([key]) => (
          <CustomTableCell key={key}>
            {getTargetValue(eligibles, metTargets, key, 3, showAllTargets, componentCode)}
          </CustomTableCell>
        ))}
        <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
      </CustomTableRow>

      <CustomTableRow>
        <RowLabelCell>{`2 Points`}</RowLabelCell>
        {Object.entries(indicatorsToUse).map(([key]) => (
          <CustomTableCell key={key}>
            {getTargetValue(eligibles, metTargets, key, 2, showAllTargets, componentCode)}
          </CustomTableCell>
        ))}
        <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
      </CustomTableRow>

      <CustomTableRow>
        <RowLabelCell>{`1 Point`}</RowLabelCell>
        {Object.entries(indicatorsToUse).map(([key]) => (
          <CustomTableCell key={key}>
            {getTargetValue(eligibles, metTargets, key, 1, showAllTargets, componentCode)}
          </CustomTableCell>
        ))}
        <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
      </CustomTableRow>

      <CustomTableRow>
        <RowLabelCell className={classes.boldCell}>Earned</RowLabelCell>

        {Object.entries(indicatorsToUse).map(([key]) => {
          const mt = metTargets && metTargets[key] ? metTargets[key] : { point: '-' };
          const point = mt.point;
          const isEligible = eligibles && eligibles[key];

          const activeClass = point !== '-' ? classes[`circle_${point}`] : null;

          const disabledClass = !isEligible && point !== '-' ? classes[`circle_disabled_${point}`] : null;

          return (
            <CustomTableCell className={classes.boldCell} key={key}>
              <div className={classNames(classes.circle, activeClass, disabledClass)}>
                <div className={classNames(classes.circle_Number, !isEligible && classes.circle_NumberDisabled)}>
                  {point}
                </div>
              </div>
            </CustomTableCell>
          );
        })}

        <EmptyCols length={totalsColumnsLength + scoreColumnsLength} />
      </CustomTableRow>
    </React.Fragment>
  );
};
SubComponentRows.propTypes = {
  label: PropTypes.string.isRequired,
  counts: PropTypes.object.isRequired,
  totals: PropTypes.object.isRequired,
  percents: PropTypes.object.isRequired,
  targets: PropTypes.object.isRequired,
  totalIndicators: PropTypes.object.isRequired,
  eligibles: PropTypes.object.isRequired,
  metTargets: PropTypes.object.isRequired,
  classes: PropTypes.any,
  totalsColumnsLength: PropTypes.number.isRequired,
  scoreColumnsLength: PropTypes.number.isRequired,
  handleGetStudentsList: PropTypes.func,
  studentsListPath: PropTypes.string,
  studentsListPathDoesNotMeet: PropTypes.string,
  profile: PropTypes.object,
  schoolTypeConversion: PropTypes.string,
  showAllTargets: PropTypes.bool,
  showTenths: PropTypes.bool,
  d2aTables: PropTypes.object,
  displayedIndicators: PropTypes.object,
};

export default withStyles(styles)(SubComponentRows);
